import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import styles from './W_ProfileTab.module.css';
import cn from 'classnames';
import A_Input from '@/components/A_Atoms/A_Input/A_Input';
import A_TextArea from '@/components/A_Atoms/A_TextArea/A_TextArea';
import A_ToggleButton from '@/components/A_Atoms/A_ToggleButton/A_ToggleButton';
import A_SmallInput from '@/components/A_Atoms/A_SmallInput/A_SmallInput';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import O_MediatorSummaryCard from '@/components/O_Organisms/O_MediatorSummaryCard/O_MediatorSummaryCard';
import M_SocialInput from '@/components/M_Molecules/M_SocialInput/M_SocialInput';
import { User } from '@/types';
import M_QRCodeContainer from '@/components/M_Molecules/M_QRCodeContainer/M_QRCodeContainer';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import { copyToClipboard } from '@/utils/clipboard';
import {
  formatDateDDMMYYYY,
  formatDateInputToDDMMYYYY,
  formatLocalDateToYYYYMMDD,
  parseDDMMYYYYToISO,
} from '@/utils/date';
import S_DateBirthPopup from '@/components/S_Super-Organisms/S_DateBirthPopup/S_DateBirthPopup';
import { useSocialLinks } from '@/hooks/useSocialLinks';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';

interface W_ProfileTabProps {
  user: User;
  onDirtyChange?: (isDirty: boolean) => void;
}

export interface ProfileTabHandle {
  resetChanges: () => void;
  saveChanges: () => Promise<void>;
}

interface ProfileFormData {
  userTag: string;
  firstName: string;
  lastName: string;
  middleName: string;
  bio: string;
  location: string;
  dateOfBirth: string;
  gender: 'male' | 'female' | 'not_selected';
  country: string;
  city: string;
  postalCode: string;
  residenceCountry: string;
  residenceCity: string;
  residencePostalCode: string;
}

const areFormStatesEqual = (a: ProfileFormData, b: ProfileFormData) =>
  JSON.stringify(a) === JSON.stringify(b);

const W_ProfileTab = forwardRef<ProfileTabHandle, W_ProfileTabProps>(
  ({ user, onDirtyChange }, ref) => {
    const contactSocials = user.contact?.socials ?? [];

    const {
      links: socialLinks,
      saved: savedLinks,
      original: originalLinks,
      icons: platformIcons,
      add: addSocialLink,
      update: updateSocialLink,
      remove: removeSocialLink,
      save: saveSocialLink,
    } = useSocialLinks(
      contactSocials.length ? contactSocials.map(link => link.url) : ['']
    );
    const [isQRCodeVisible, setIsQRCodeVisible] = useState(false);
    const [isDateBirthPopupOpen, setIsDateBirthPopupOpen] = useState(false);
    const [dateOfBirthInput, setDateOfBirthInput] = useState('');
    const isMobileWidth = useIsMobile(520);
    const { language } = useLanguage();

    const initialFormData: ProfileFormData = useMemo(
      () => ({
        userTag: user.handle ? `@${user.handle}` : `@${user.id}`,
        firstName: user.personal?.firstName || '',
        lastName: user.personal?.lastName || '',
        middleName: user.personal?.middleName || '',
        bio: user.personal?.bio || '',
        location: user.location?.label || '',
        dateOfBirth: user.personal?.dateOfBirth || '',
        gender: user.personal?.gender || 'not_selected',
        country: user.location?.birthPlace?.country || '',
        city: user.location?.birthPlace?.city || '',
        postalCode: user.location?.birthPlace?.postalCode || '',
        residenceCountry: user.location?.residencePlace?.country || '',
        residenceCity: user.location?.residencePlace?.city || '',
        residencePostalCode: user.location?.residencePlace?.postalCode || '',
      }),
      [user]
    );

    const handleOpenDateBirthPopup = () => {
      setIsDateBirthPopupOpen(true);
    };

    const handleSelectDate = (date: Date) => {
      updateField('dateOfBirth', formatLocalDateToYYYYMMDD(date));
    };

    const [formData, setFormData] = useState<ProfileFormData>(initialFormData);
    const [baselineData, setBaselineData] =
      useState<ProfileFormData>(initialFormData);

    useEffect(() => {
      setFormData(initialFormData);
      setBaselineData(initialFormData);
      setDateOfBirthInput(formatDateDDMMYYYY(initialFormData.dateOfBirth));
    }, [initialFormData]);

    useEffect(() => {
      setDateOfBirthInput(formatDateDDMMYYYY(formData.dateOfBirth));
    }, [formData.dateOfBirth]);

    const isDirty = useMemo(
      () => !areFormStatesEqual(formData, baselineData),
      [formData, baselineData]
    );

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => {
      return () => {
        onDirtyChange?.(false);
      };
    }, [onDirtyChange]);

    const handleResetChanges = useCallback(() => {
      setFormData(baselineData);
    }, [baselineData]);

    const handleSaveChanges = useCallback(async () => {
      try {
        const handleValue = formData.userTag.replace(/^@+/, '').trim();
        const updateData = {
          ...(handleValue && { handle: handleValue }),
          personal: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            middleName: formData.middleName || '',
            bio: formData.bio || '',
            dateOfBirth: formData.dateOfBirth || null,
            gender: formData.gender,
          },
          location: {
            birthPlace: {
              country: formData.country || '',
              city: formData.city || '',
              postalCode: formData.postalCode || '',
            },
            residencePlace: {
              country: formData.residenceCountry || '',
              city: formData.residenceCity || '',
              postalCode: formData.residencePostalCode || '',
            },
          },
        };

        const { profileApi: profileApiService } = await import(
          '@/services/profileApi'
        );
        const response = await profileApiService.updateProfile(updateData);
        if (response.success && response.data) {
          const updatedFormData: ProfileFormData = {
            userTag: response.data.handle
              ? `@${response.data.handle}`
              : `@${response.data.id}`,
            firstName: response.data.personal?.firstName || '',
            lastName: response.data.personal?.lastName || '',
            middleName: response.data.personal?.middleName || '',
            bio: response.data.personal?.bio || '',
            location: '',
            dateOfBirth: response.data.personal?.dateOfBirth || '',
            gender: (response.data.personal?.gender as any) || 'not_selected',
            country: response.data.location?.birthPlace?.country || '',
            city: response.data.location?.birthPlace?.city || '',
            postalCode: response.data.location?.birthPlace?.postalCode || '',
            residenceCountry:
              response.data.location?.residencePlace?.country || '',
            residenceCity: response.data.location?.residencePlace?.city || '',
            residencePostalCode:
              response.data.location?.residencePlace?.postalCode || '',
          };
          setFormData(updatedFormData);
          setBaselineData(updatedFormData);
        }
      } catch {
        // ошибка сохранения профиля
      }
    }, [formData]);

    useImperativeHandle(
      ref,
      () => ({
        resetChanges: handleResetChanges,
        saveChanges: handleSaveChanges,
      }),
      [handleResetChanges, handleSaveChanges]
    );

    const updateField = (field: keyof ProfileFormData, value: string) => {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    };

    const updateGender = (gender: 'male' | 'female' | 'not_selected') => {
      setFormData(prev => ({
        ...prev,
        gender,
      }));
    };

    const handleDateOfBirthInputChange = (value: string) => {
      const masked = formatDateInputToDDMMYYYY(value);
      setDateOfBirthInput(masked);
      const iso = parseDDMMYYYYToISO(masked);
      if (iso) updateField('dateOfBirth', iso);
    };

    const handleCopyID = async () => {
      await copyToClipboard(user.handle || user.id);
    };

    const handleOpenQR = () => {
      setIsQRCodeVisible(true);
    };

    return (
      <div className={styles.wrapper}>
        <div className={styles.about_user_wrapper}>
          <h4 className={styles.title}>
            {getTranslation(language, 'profileTab.username')}
          </h4>
          <div className={styles.id_wrapper}>
            <A_SmallInput
              label=''
              value={formData.userTag}
              onChange={value => updateField('userTag', value)}
              placeholder={getTranslation(
                language,
                'profileTab.usernamePlaceholder'
              )}
              background='gray'
            />
            <div className={styles.id_buttons_wrapper}>
              <A_CircleButton
                icon={<Q_Icon.CopyIcon width={24} height={24} fill='none' />}
                variant='secondary'
                onClick={handleCopyID}
              />
              <A_CircleButton
                icon={<Q_Icon.QRIcon width={24} height={24} fill='none' />}
                variant='secondary'
                onClick={handleOpenQR}
              />
            </div>
          </div>
          <div className={styles.fio_inputs_wrapper}>
            <A_Input
              label={getTranslation(language, 'profileTab.firstName')}
              value={formData.firstName}
              onChange={value => updateField('firstName', value)}
              type='text'
              maxLength={40}
              borderRadius='text-fields'
              background='gray'
            />
            <A_Input
              label={getTranslation(language, 'profileTab.middleName')}
              value={formData.middleName}
              onChange={value => updateField('middleName', value)}
              type='text'
              maxLength={40}
              borderRadius='text-fields'
              background='gray'
            />
            <A_Input
              label={getTranslation(language, 'profileTab.lastName')}
              value={formData.lastName}
              onChange={value => updateField('lastName', value)}
              type='text'
              maxLength={40}
              borderRadius='text-fields'
              background='gray'
            />
            <A_TextArea
              label={getTranslation(language, 'profileTab.bio')}
              value={formData.bio}
              onChange={value => updateField('bio', value)}
              height={234}
              maxLength={142}
              background='gray'
            />
          </div>
        </div>
        <div className={styles.about_user_wrapper}>
          <h4 className={styles.title}>
            {getTranslation(language, 'profileTab.personalInfo')}
          </h4>
          <div className={styles.gender_wrapper}>
            <span className={cn(styles.toggle_button_label, 'text-p-2')}>
              {getTranslation(language, 'profileTab.gender')}
            </span>
            <div className={styles.toggle_button_wrapper}>
              <A_ToggleButton
                isActive={formData.gender === 'not_selected'}
                onClick={() => updateGender('not_selected')}
                title={
                  isMobileWidth
                    ? ''
                    : getTranslation(language, 'profileTab.genderNotSelected')
                }
                icon={
                  isMobileWidth ? (
                    <Q_Icon.NoChosenIcon width={24} height={24} fill='none' />
                  ) : undefined
                }
                bgColor='white'
              />
              <A_ToggleButton
                isActive={formData.gender === 'male'}
                onClick={() => updateGender('male')}
                title={getTranslation(language, 'profileTab.male')}
                bgColor='white'
              />
              <A_ToggleButton
                isActive={formData.gender === 'female'}
                onClick={() => updateGender('female')}
                title={getTranslation(language, 'profileTab.female')}
                bgColor='white'
              />
            </div>
          </div>
          <div className={styles.date_of_birth_wrapper}>
            <span className={cn(styles.toggle_button_label, 'text-p-2')}>
              {getTranslation(language, 'profileTab.birthday')}
            </span>
            <div className={styles.date_of_birth_inner_wrapper}>
              <A_SmallInput
                label={getTranslation(language, 'profileTab.datePlaceholder')}
                value={dateOfBirthInput}
                onChange={handleDateOfBirthInputChange}
                placeholder='DD.MM.YYYY'
                background='gray'
                maxLength={10}
              />
              <A_CircleButton
                icon={
                  <Q_Icon.CalendarIcon width={24} height={24} fill='none' />
                }
                variant='secondary'
                onClick={handleOpenDateBirthPopup}
              />
            </div>
          </div>
        </div>
        <div className={styles.about_user_wrapper}>
          <h4 className={styles.title}>
            {getTranslation(language, 'profileTab.birthPlace')}
          </h4>
          <A_Input
            label={getTranslation(language, 'profileTab.country')}
            value={formData.country}
            onChange={value => updateField('country', value)}
            type='text'
            borderRadius='text-fields'
            background='gray'
          />
          <A_Input
            label={getTranslation(language, 'profileTab.city')}
            value={formData.city}
            onChange={value => updateField('city', value)}
            type='text'
            borderRadius='text-fields'
            background='gray'
          />
          <div className={styles.small_plate_wrapper}>
            <span className={cn(styles.small_plate_label, 'text-p-7')}>
              {getTranslation(language, 'profileTab.postalCode')}
            </span>
            <A_SmallInput
              label={getTranslation(language, 'profileTab.postalCode')}
              value={formData.postalCode}
              onChange={value => updateField('postalCode', value)}
              background='gray'
              maxLength={9}
            />
          </div>
        </div>
        <div className={styles.about_user_wrapper}>
          <h4 className={styles.title}>
            {getTranslation(language, 'profileTab.residencePlace')}
          </h4>
          <A_Input
            label={getTranslation(language, 'profileTab.country')}
            value={formData.residenceCountry}
            onChange={value => updateField('residenceCountry', value)}
            type='text'
            borderRadius='text-fields'
            background='gray'
          />
          <A_Input
            label={getTranslation(language, 'profileTab.city')}
            value={formData.residenceCity}
            onChange={value => updateField('residenceCity', value)}
            type='text'
            borderRadius='text-fields'
            background='gray'
          />
          <div className={styles.small_plate_wrapper}>
            <span className={cn(styles.small_plate_label, 'text-p-7')}>
              {getTranslation(language, 'profileTab.postalCode')}
            </span>
            <A_SmallInput
              label={getTranslation(language, 'profileTab.postalCode')}
              value={formData.residencePostalCode}
              onChange={value => updateField('residencePostalCode', value)}
              background='gray'
              maxLength={9}
            />
          </div>
        </div>
        <div className={styles.about_user_wrapper}>
          <h4 className={styles.title}>
            {getTranslation(language, 'profileTab.socials')}
          </h4>
          {socialLinks.map((link, index) => {
            const isSaved = savedLinks.has(index);
            const showSaveButton = link.length > 0 && !isSaved;
            const showDeleteButton = index > 0 && !isSaved;

            return (
              <M_SocialInput
                key={index}
                value={link}
                onChange={value => updateSocialLink(index, value)}
                isFirst={index === 0}
                showSaveButton={showSaveButton}
                showDeleteButton={showDeleteButton}
                isSaved={isSaved}
                originalValue={originalLinks[index]}
                platformIcon={platformIcons[index]}
                onSave={() => saveSocialLink(index)}
                onDelete={() => removeSocialLink(index)}
              />
            );
          })}
          <A_CircleButton
            icon={
              <Q_Icon.PlusIcon width={24} height={24} fill='currentColor' />
            }
            variant='tertiary'
            onClick={addSocialLink}
          />
        </div>
        <O_MediatorSummaryCard />
        <div className={styles.qr_wrapper}>
          {isQRCodeVisible && <M_QRCodeContainer />}
        </div>
        {isDateBirthPopupOpen && (
          <S_DateBirthPopup
            setIsDateBirthPopupOpen={setIsDateBirthPopupOpen}
            onSelectDate={handleSelectDate}
            initialDate={
              formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined
            }
          />
        )}
      </div>
    );
  }
);

export default W_ProfileTab;
